import React, { Fragment } from 'react';
import ArkLoader from '@src/js/common/ark-extensions';
import componentConfig from '@src/js/common/ark-extensions/dc-components/color-picker/component-config';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import {
    ColorPickerEntry,
    ColorPickerItemType, ColorPickerType, ColorSourceType, ColorStyleType, IColorPickerColor,
    IColorPickerExtension, IColorSelectExtraOptions, IItemList, ILumItem,
    IShowOptions,
    ISlideShowOptions,
    THEME_ITEM_KEY,
} from './type';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import {
    IColorPickerManager,
} from '@src/slide/application/service/color-picker-manager/color-picker-manager.interface';
import ColorPicker from '@com-pc/color-picker';
import Menu from '@tencent/dui/lib/components/Menu';
import i18n from '@i18next/i18n';
import ColorPickerIcon from '@com-pc/palette-panel/images/color-picker.svg';
import { IMenuManager } from '@src/slide/application/service/menu-manager/menu-manager.interface';
import { FillMenuIcon } from '@framework/workbench/ui/fill-color/fill-menu-icon';
import './color-picker-container.less';
import {
    blackThemeLum,
    colorThemeLum,
    darkThemeLum,
    lightThemeLum,
    whiteThemeLum,
} from '@src/js/common/ark-extensions/dc-components/color-picker/color-panel-template';
import { IReactionDisposer } from 'mobx/lib/internal';
import { TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { browser } from '@utils/agent';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { STColorSchemeIndex } from '@tencent/slide-sdk-pb-model/src/ultrabuf/ultrabuf.types';
import { IStatePointEnum } from '@src/slide/application/service/slide-render-state/slide-render-state-manager.interface';

const LIGHT_COLOR_VALUE = 211;
const DARK_COLOR_VALUE = 54;
const WHITE_COLOR_VALUE = 255;
const BLACK_COLOR_VALUE = 0;

interface IColorPickerContainerState {
    loaded: boolean;
}

/**
 * 新dc色盘
 * 可以通过正常react组件调用，也可以通过static api来全局调用
 */
export default class ColorPickerContainer extends React.Component<ISlideShowOptions, IColorPickerContainerState> {
    private colorPickerExtension?: React.ComponentType<IShowOptions> | null;
    private themeModelReaction: IReactionDisposer;

    public constructor(opt: ISlideShowOptions) {
        super(opt);
        this.colorPickerExtension = null;
        this.state = {
            loaded: false,
        };
    }

    public componentWillUnmount() {
        this.themeModelReaction?.();
    }

    public async componentDidMount() {
        await this.getSlideRenderStateManager()
            ?.getStatePoints()[IStatePointEnum.visibleSlideRender].getPointCompletePromise();
        const colorPickerService = await ArkLoader.extensionService
            .getExtension<IColorPickerExtension>(componentConfig.name);
        this.colorPickerExtension = colorPickerService.getRender();
        if (this.colorPickerExtension) {
            this.setState({
                loaded: true,
            });
        }
    }

    public transpose(matrix) {
        const row = matrix.length;
        const col = matrix[0].length;
        const transposed = new Array(col);
        for (let i = 0; i < col; i++) {
            transposed[i] = new Array(row);
            for (let j = 0; j < row; j++) {
                transposed[i][j] = matrix[j][i];
            }
        }
        return transposed;
    }

    /* eslint-disable complexity */
    public render(): React.ReactNode {
        const Palette = this.colorPickerExtension;
        const useNewEyeDropper = !!this.getColorPickerManager()?.useNewEyeDropper;
        if (this.state.loaded && Palette) {
            const { selectedTdColor, selectedGradientColor } = this.props;
            let itemList: IItemList = [];
            itemList = itemList.concat(this.props.itemList || []);

            if (!useNewEyeDropper && !browser.isMobile) {
                itemList = this.getCustomEyeDropper(itemList, this.props.onColorChange);
            }

            if (!this.props.isStandardColorPicker && this.isLegalTheme()) {
                itemList = this.getThemeColorItemList(this.props.themePanelLabel).concat(itemList);
            }

            const cachedElement = document.activeElement as HTMLDivElement;
            let selectedToken;
            let selectedColor;
            if (selectedTdColor !== 'multi') {
                if (selectedTdColor?.alpha !== 1) {
                    selectedColor = selectedTdColor?.rgba.toLowerCase();
                } else {
                    // 自定义
                    selectedColor = selectedTdColor?.getColorWithNoAlpha()?.toUpperCase();
                }

                // 主题
                if (typeof selectedTdColor?.colorValue === 'number') {
                    selectedToken = `${selectedTdColor?.colorValue}_${selectedTdColor?.getColorWithNoAlpha()}`.toUpperCase();
                }

                // 渐变
                if (selectedGradientColor)  {
                    selectedColor = selectedGradientColor;
                    selectedToken = undefined;
                }
            }

            return (<div
                onMouseLeave={this.props.onMouseLeave}
                onClick={this.props.onClick}>
                <Palette
                    {...this.props}
                    selectedColor={selectedColor}
                    selectedToken={selectedToken}
                    onColorChange={(color: string, from: ColorSourceType, options?: IColorSelectExtraOptions) => {
                        if (document.activeElement !== cachedElement && from !== ColorSourceType.Custom) {
                            cachedElement?.focus();
                        }


                        if (this.isGradualColor(color)) {
                            this.props.onGradualColorChange?.(color, from);
                            return;
                        }

                        let tdColor: TDColor;
                        const { lum, theme } = options || {};
                        if (theme) {
                            tdColor = new TDColor({
                                rgbaStr: color,
                                themeKey: theme.val,
                                lumMod: lum?.lumMod,
                                lumOff: lum?.lumOff,
                            });
                        } else {
                            tdColor = new TDColor({
                                rgbaStr: color,
                            });
                        }

                        this.props.onColorChange?.(tdColor, from);
                    }}
                    onEyedropperVisibleChange={(visible) => {
                        if (visible) {
                            cachedElement?.blur();
                        } else {
                            if (document.activeElement !== cachedElement) {
                                cachedElement?.focus();
                            }
                        }
                    }}
                    eyedropperDisabled={browser.isMobile || !useNewEyeDropper}
                    itemList={itemList}
                />
            </div>);
        }
        return null;
    }

    private isGradualColor(color: string): boolean {
        return color?.startsWith('linear-gradient');
    }

    private getThemeColorItemList(themePanelLabel?: string): IItemList {
        const parseLumList = this.getColorPanelLumConfig();
        const themeItemList = this.getThemeItems();

        // 移动端不展示tips
        if (browser.isMobile) {
            themeItemList.forEach((item) => {
                item.tips = undefined;
            });
        }

        return [
            {
                itemType: ColorPickerItemType.Color,
                payload: {
                    label: themePanelLabel,
                    styleName: ColorStyleType.ThemeWithLum,
                    overrideTheme: true,
                    themeItems: themeItemList,
                    lumItems: parseLumList,
                    theme: THEME_ITEM_KEY,
                },
            },
        ];
    }

    private getSelectionService(): ISelectionService | undefined {
        return (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
            .viewableServiceLayer?.selectionService;
    }

    private getColorPickerManager(): IColorPickerManager | undefined {
        return (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
            .editableServiceLayer?.colorPickerManager;
    }

    private getMenuManager(): IMenuManager | undefined {
        return (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
            .editableServiceLayer?.menuManager;
    }

    private getPresentation() {
        return (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
            .getUiDataService().presentation;
    }

    private getThemeColorManager() {
        return (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
            .editableServiceLayer?.themeColorManager;
    }

    private getSlideRenderStateManager() {
        return (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
            .viewableServiceLayer?.slideRenderStateManager;
    }

    /**
     * 获取主题色
     * @private
     */
    private getThemeItems() {
        const currentSlide = this.getSelectionService()?.currentSlide;
        const isDark = currentSlide?.getRelationMasterModel().clrMap.tx1 === STColorSchemeIndex.lt1;
        if (this.props.entry === ColorPickerEntry.ANIM) {
            const list = this.getThemeColorManager()?.currentThemeModel
                ?.getThemeColorList(isDark, STColorSchemeIndex.folHlink)
                ?.filter(item => item.val < STColorSchemeIndex.accent5 || item.val > STColorSchemeIndex.accent6);
            return list || [];
        }
        return this.getThemeColorManager()?.currentThemeModel?.getThemeColorList(isDark) || [];
    }

    private getCustomEyeDropper = (itemList, handleColorChange?) => {
        itemList.push({
            itemType: ColorPickerItemType.Menu,
            payload: {
                menus: <Fragment>
                    <Menu.Item
                        icon={<FillMenuIcon icon={ColorPickerIcon} />}
                        onClick={(e) => {
                            this.showCustomEyeDropper(e, handleColorChange);
                        }}
                        style={{
                            paddingLeft: 12,
                        }}
                    >
                        { i18n.t('取色器') }
                    </Menu.Item>
                </Fragment>,
            },
        });

        return itemList;
    };

    private showCustomEyeDropper = (e, handleColorChange?) => {
        const dom = document.getElementById('PopupWrapper');
        ColorPicker.show(dom, {
            handleColorChange,
            point: {
                clientX: e.clientX,
                clientY: e.clientY,
            },
            onVisibleChange: (visible) => {
                const menuManager = this.getMenuManager();
                if (menuManager) {
                    menuManager.isColorPicking = visible;
                }
            },
        });
    };

    /**
     * 查看是否是合法主题
     */
    private isLegalTheme = (): boolean => {
        const themeList = this.getThemeItems();
        let flag = true;
        themeList?.forEach((themeItem) => {
            if (!themeItem?.color) {
                flag = false;
            }
        });
        return flag;
    };

    /**
     * 根据原始主题色组装色盘所有的发光
     * 切换当前主题时才应该更新
     */
    private getColorPanelLumConfig = (): ILumItem[][] => {
        if (this.props.entry === ColorPickerEntry.ANIM) return [];
        const themeList = this.getThemeItems();
        const parseLumList: ILumItem[][] = [];
        themeList.forEach((themeItem, arrangeIndex) => {
            const { color } = themeItem;
            const lumList = this.getLumByColor(color || '');
            lumList.forEach((lumItem, rowIndex) => {
                if (!parseLumList[rowIndex]) {
                    parseLumList[rowIndex] = [];
                }
                parseLumList[rowIndex][arrangeIndex] = lumItem;
            });
        });
        return parseLumList;
    };

    /**
     * 根据颜色是黑/白/深/浅获取对应的发光
     * @param colorString
     */
    private getLumByColor = (colorString: string) => {
        const averageColorRgbNumber = this.getRgbAverageValueByHex(colorString);
        if (averageColorRgbNumber === WHITE_COLOR_VALUE) {
            return whiteThemeLum;
        }

        if (averageColorRgbNumber === BLACK_COLOR_VALUE) {
            return blackThemeLum;
        }

        if (averageColorRgbNumber > LIGHT_COLOR_VALUE) {
            return lightThemeLum;
        }

        if (averageColorRgbNumber < DARK_COLOR_VALUE) {
            return darkThemeLum;
        }

        return colorThemeLum;
    };

    /**
     * 获取16进制色值的rgb平均值，用于判断是深色还是浅色
     * @param hexString
     */
    private getRgbAverageValueByHex = (hexString: string): number => {
        const numbers = hexString.match(/\w\w/g)?.map(x => parseInt(x, 16));
        let r = 0;
        let g = 0;
        let b = 0;
        if (numbers?.length) {
            r = numbers[0] || r;
            g = numbers[1] || g;
            b = numbers[2] || b;
        }

        return Math.ceil((r + g + b) / 3);
    };
}
